import css from "./lotmap.scss?inline";
import mapcss from "../../utils/maps/maps.scss?inline";
import globalStyles from "../../index.scss?inline";
import { Component } from "../../utils/Component";
import { ArgSpecDictionary } from "../component-utils";

import gsap from "gsap";

import { createSVGOverlay } from "./SVGOverlay";

import { Lot } from "./Lot";
import { mapHelper } from "../../utils/maps/map-helper";
import {
	DEFAULT_SW_LAT,
	DEFAULT_SW_LNG,
	DEFAULT_NE_LAT,
	DEFAULT_NE_LNG,
	LotApiResponse,
	MAPS_API_KEY,
} from "./LotMap.constants";
import {
	HHDSMapPopup,
	HHDSMapPopupEvent,
	HHDSMapPopupHAlign,
	HHDSMapPopupTagName,
	HHDSMapPopupVAlign,
} from "../MapPopup/MapPopup";
import {
	getAvailableLotContent,
	getModelHomeContent,
	getPlanContent,
} from "./LotMapPopups";
import { HHDSSegmentedControl } from "../SegmentedControl/SegmentedControl";
import {
	CanvasOverlayType,
	createCanvasOverlay,
} from "../../utils/maps/CanvasOverlay";

export enum PrimaryLotType {
	model = "model",
	available = "available",
	quickmove = "quickmove",
	sold = "sold",
	unavailable = "unavailable",
	inactive = "inactive",
}

const OVERLAY_URL =
	"/wp-content/themes/hhh-teravalis/assets/images/features.svg";
const LOT_OUTLINES_URL =
	"/wp-content/themes/hhh-teravalis/data/parcels/hh-lots-outline.json";
const API_LOTMAP_ENDPOINT = "/wp-json/teravalis/v1/lotmap";
const OPEN_POPUP_FOR_FOCUSED_LOT: boolean = false;

const MIN_ZOOM: number = 16;
const MAX_ZOOM: number = 20;

const DEBUG_VERBOSE: boolean = false;
const CLASS_NAME: string = "HHDSLotMap";
export const HHDSLotMapTagName: string = "hhds-lotmap";
const TAG_NAME: string = HHDSLotMapTagName;

export const HHDSLotMapAttrNames = {
	image: "image",
	lot: "lot",
	lotmarker: "lotmarker",
	builder: "builder",
	collection: "collection",
	geojson: "geojson",
	switcher: "switcher",
	parcel: "parcel",
	controls: "controls",
};

const Attrs = HHDSLotMapAttrNames;
export class HHDSLotMap extends Component {
	private container: HTMLElement | null = null;
	private map: any;
	private minimap: HTMLElement | null = null;
	private legend: HTMLElement | null = null;
	private popup: HHDSMapPopup | null = null;

	private numLots: number = 0;

	private outlineLayer: any;
	private lotLayer: any;

	private activeLot: Lot | null = null;

	private lots = new WeakMap<google.maps.Data.Feature, Lot>();

	private loading: boolean = true;

	// ----------------------------------------------------------------------
	// Initialisation
	// ----------------------------------------------------------------------

	protected override async init(): Promise<void> {
		this.shadow.innerHTML = `
      <style>${globalStyles}</style>
			<style>${css}</style>
      <style>${mapcss}</style>
        <div class="${TAG_NAME}">
			<div class="hhds-lotmap-container"></div>
			${this.switcherHTML}

			<div class="hhds-lotmap-legend">
				${this.legendHTML}  
			</div>

		  	<div class="hhds-lotmap-minimap">
				<hhds-minimap></hhds-minimap>
			</div>

          <div class="container">
            ${mapHelper.popupHtml(
							HHDSMapPopupHAlign.right,
							HHDSMapPopupVAlign.bottom
						)}
          </div>


        </div>
		`;

		// Get handles to the elements
		this.container = this.shadow.querySelector(
			".hhds-lotmap-container"
		) as HTMLElement;

		this.minimap = this.shadow.querySelector(
			".hhds-lotmap-minimap"
		) as HTMLElement;

		this.legend = this.shadow.querySelector(
			".hhds-lotmap-legend"
		) as HTMLElement;

		// Load Google Maps
		await this.initGoogleMaps();

		// Set the default bounds
		this.setMapBounds();

		// Draw the lots
		await this.drawLots();

		// Draw Lot outlines
		await this.drawLotOutlines();

		// Load the SVG overlay
		await this.setupOverlay();

		// Configure the Popup
		this.configurePopup();

		// Setup the Switcher
		this.setupSwitcher();

		// Zoom to the center point

		// Check if the lotLayer has any features
		if (this.numLots > 0) {
			mapHelper.zoomToCenterOfLayerBounds(this.lotLayer);
		} else {
			mapHelper.zoomToCenterOfLayerBounds(this.outlineLayer);
		}

		// Finally, set our loading flag
		this.loading = false;

		// Get a handle to the minimap
		this.minimap = this.shadow.querySelector("hhds-minimap") as HTMLElement;

		setTimeout(() => {
			this.animateIn();
		}, 500);

		this.map.addListener("zoom_changed", () => {
			DEBUG_VERBOSE && console.log(this.map.getZoom());
		});
	}

	// ----------------------------------------------------------------------
	// Google Maps
	// ----------------------------------------------------------------------

	async initGoogleMaps() {
		const container = this.shadow.querySelector(
			".hhds-lotmap-container"
		) as HTMLElement;
		this.map = await mapHelper.createMap({
			apiKey: MAPS_API_KEY,
			mapId: "b3c99dad8deb859f",
			container: container,
			minZoom: MIN_ZOOM,
			maxZoom: MAX_ZOOM,
			zoomControl: this.vars.get<boolean>("controls"),
		});
		window.addEventListener("resize", () => {
			if (this.map) google.maps.event.trigger(this.map, "resize");
		});
	}

	// ----------------------------------------------------------------------
	// Legend
	// ----------------------------------------------------------------------

	get legendHTML(): string {
		return `
    
      <svg width="145" height="154" viewBox="0 0 145 154" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g filter="url(#filter0_d_4090_40850)">
        <rect width="113" height="122" transform="translate(16 12)" fill="white"/>
        <circle cx="30" cy="29" r="6" fill="#47BAA3"/>
        <path d="M40.4763 33L43.6923 24.72H45.1923L48.3963 33H46.9803L44.6043 26.592C44.5723 26.504 44.5443 26.424 44.5203 26.352C44.5043 26.28 44.4763 26.196 44.4363 26.1C44.4123 26.196 44.3883 26.28 44.3643 26.352C44.3403 26.424 44.3123 26.5 44.2803 26.58L41.8803 33H40.4763ZM42.3963 30.888V29.724H46.6083V30.888H42.3963ZM50.4427 33L48.1867 27.108H49.5787L51.0907 31.236C51.1227 31.332 51.1507 31.416 51.1747 31.488C51.2067 31.552 51.2387 31.632 51.2707 31.728C51.3027 31.632 51.3307 31.548 51.3547 31.476C51.3867 31.404 51.4147 31.324 51.4387 31.236L52.9027 27.108H54.2707L52.0027 33H50.4427ZM56.5465 33.12C55.9305 33.12 55.4465 32.968 55.0945 32.664C54.7425 32.36 54.5665 31.944 54.5665 31.416C54.5665 31.08 54.6425 30.796 54.7945 30.564C54.9545 30.332 55.1905 30.144 55.5025 30C55.8145 29.848 56.2105 29.736 56.6905 29.664L57.6385 29.508C57.9425 29.46 58.1545 29.392 58.2745 29.304C58.3945 29.216 58.4545 29.08 58.4545 28.896C58.4545 28.64 58.3425 28.432 58.1185 28.272C57.9025 28.112 57.6025 28.032 57.2185 28.032C56.8905 28.032 56.5705 28.096 56.2585 28.224C55.9545 28.352 55.6785 28.54 55.4305 28.788L54.7585 27.972C55.0705 27.644 55.4425 27.4 55.8745 27.24C56.3065 27.072 56.7825 26.988 57.3025 26.988C58.0785 26.988 58.6745 27.172 59.0905 27.54C59.5065 27.9 59.7145 28.408 59.7145 29.064V31.512C59.7145 31.848 59.7425 32.124 59.7985 32.34C59.8545 32.548 59.9585 32.768 60.1105 33H58.8145C58.7185 32.856 58.6465 32.72 58.5985 32.592C58.5585 32.456 58.5305 32.28 58.5145 32.064C58.3785 32.288 58.2105 32.48 58.0105 32.64C57.8105 32.8 57.5865 32.92 57.3385 33C57.0985 33.08 56.8345 33.12 56.5465 33.12ZM56.8465 32.124C57.1585 32.124 57.4345 32.06 57.6745 31.932C57.9225 31.804 58.1145 31.628 58.2505 31.404C58.3865 31.172 58.4545 30.908 58.4545 30.612V30.12C58.3665 30.184 58.2665 30.236 58.1545 30.276C58.0505 30.316 57.9105 30.352 57.7345 30.384L56.9545 30.492C56.5705 30.556 56.2865 30.652 56.1025 30.78C55.9185 30.908 55.8265 31.092 55.8265 31.332C55.8265 31.596 55.9145 31.796 56.0905 31.932C56.2665 32.06 56.5185 32.124 56.8465 32.124ZM61.328 33V27.108H62.624V33H61.328ZM61.976 26.232C61.752 26.232 61.568 26.16 61.424 26.016C61.28 25.864 61.208 25.68 61.208 25.464C61.208 25.248 61.28 25.068 61.424 24.924C61.568 24.78 61.752 24.708 61.976 24.708C62.2 24.708 62.384 24.78 62.528 24.924C62.672 25.068 62.744 25.248 62.744 25.464C62.744 25.68 62.672 25.864 62.528 26.016C62.384 26.16 62.2 26.232 61.976 26.232ZM64.2811 33V24.72H65.5771V33H64.2811ZM68.7223 33.12C68.1063 33.12 67.6223 32.968 67.2703 32.664C66.9183 32.36 66.7423 31.944 66.7423 31.416C66.7423 31.08 66.8183 30.796 66.9703 30.564C67.1303 30.332 67.3663 30.144 67.6783 30C67.9903 29.848 68.3863 29.736 68.8663 29.664L69.8143 29.508C70.1183 29.46 70.3303 29.392 70.4503 29.304C70.5703 29.216 70.6303 29.08 70.6303 28.896C70.6303 28.64 70.5183 28.432 70.2943 28.272C70.0783 28.112 69.7783 28.032 69.3943 28.032C69.0663 28.032 68.7463 28.096 68.4343 28.224C68.1303 28.352 67.8543 28.54 67.6063 28.788L66.9343 27.972C67.2463 27.644 67.6183 27.4 68.0503 27.24C68.4823 27.072 68.9583 26.988 69.4783 26.988C70.2543 26.988 70.8503 27.172 71.2663 27.54C71.6823 27.9 71.8903 28.408 71.8903 29.064V31.512C71.8903 31.848 71.9183 32.124 71.9743 32.34C72.0303 32.548 72.1343 32.768 72.2863 33H70.9903C70.8943 32.856 70.8223 32.72 70.7743 32.592C70.7343 32.456 70.7063 32.28 70.6903 32.064C70.5543 32.288 70.3863 32.48 70.1863 32.64C69.9863 32.8 69.7623 32.92 69.5143 33C69.2743 33.08 69.0103 33.12 68.7223 33.12ZM69.0223 32.124C69.3343 32.124 69.6102 32.06 69.8503 31.932C70.0983 31.804 70.2903 31.628 70.4263 31.404C70.5623 31.172 70.6303 30.908 70.6303 30.612V30.12C70.5423 30.184 70.4423 30.236 70.3303 30.276C70.2263 30.316 70.0863 30.352 69.9103 30.384L69.1303 30.492C68.7463 30.556 68.4623 30.652 68.2783 30.78C68.0943 30.908 68.0023 31.092 68.0023 31.332C68.0023 31.596 68.0903 31.796 68.2663 31.932C68.4423 32.06 68.6943 32.124 69.0223 32.124ZM76.8158 33.12C76.5278 33.12 76.2558 33.08 75.9998 33C75.7518 32.92 75.5238 32.804 75.3158 32.652C75.1158 32.492 74.9318 32.3 74.7638 32.076V33H73.5038V24.72H74.7998V28.092C75.0318 27.74 75.3238 27.468 75.6758 27.276C76.0278 27.084 76.4078 26.988 76.8158 26.988C77.3598 26.988 77.8438 27.124 78.2678 27.396C78.6918 27.66 79.0278 28.024 79.2758 28.488C79.5238 28.944 79.6478 29.464 79.6478 30.048C79.6478 30.488 79.5758 30.896 79.4318 31.272C79.2878 31.64 79.0878 31.964 78.8318 32.244C78.5838 32.524 78.2878 32.74 77.9438 32.892C77.5998 33.044 77.2238 33.12 76.8158 33.12ZM76.5638 32.028C76.9078 32.028 77.2118 31.944 77.4758 31.776C77.7478 31.6 77.9598 31.364 78.1118 31.068C78.2638 30.764 78.3398 30.424 78.3398 30.048C78.3398 29.672 78.2638 29.336 78.1118 29.04C77.9598 28.744 77.7478 28.512 77.4758 28.344C77.2118 28.168 76.9078 28.08 76.5638 28.08C76.3078 28.08 76.0718 28.132 75.8558 28.236C75.6398 28.332 75.4518 28.468 75.2918 28.644C75.1318 28.82 75.0078 29.028 74.9198 29.268C74.8318 29.508 74.7878 29.768 74.7878 30.048C74.7878 30.424 74.8638 30.764 75.0158 31.068C75.1678 31.364 75.3798 31.6 75.6518 31.776C75.9238 31.944 76.2278 32.028 76.5638 32.028ZM80.91 33V24.72H82.206V33H80.91ZM86.6592 33.12C86.0272 33.12 85.4712 32.992 84.9912 32.736C84.5112 32.472 84.1352 32.112 83.8632 31.656C83.5992 31.192 83.4672 30.656 83.4672 30.048C83.4672 29.608 83.5392 29.204 83.6832 28.836C83.8272 28.46 84.0352 28.136 84.3072 27.864C84.5792 27.584 84.8952 27.368 85.2552 27.216C85.6152 27.064 86.0112 26.988 86.4432 26.988C87.0192 26.988 87.5152 27.112 87.9312 27.36C88.3552 27.6 88.6792 27.94 88.9032 28.38C89.1352 28.82 89.2512 29.332 89.2512 29.916C89.2512 29.972 89.2512 30.028 89.2512 30.084C89.2512 30.132 89.2512 30.176 89.2512 30.216C89.2512 30.248 89.2472 30.284 89.2392 30.324H84.6192L84.6312 29.436H87.9792C87.9312 28.996 87.7712 28.648 87.4992 28.392C87.2352 28.128 86.8832 27.996 86.4432 27.996C86.1072 27.996 85.8112 28.084 85.5552 28.26C85.3072 28.428 85.1112 28.664 84.9672 28.968C84.8312 29.264 84.7632 29.608 84.7632 30C84.7632 30.424 84.8432 30.792 85.0032 31.104C85.1632 31.416 85.3872 31.656 85.6752 31.824C85.9712 31.992 86.3152 32.076 86.7072 32.076C87.0352 32.076 87.3392 32.024 87.6192 31.92C87.9072 31.808 88.1672 31.64 88.3992 31.416L89.0232 32.244C88.7272 32.524 88.3752 32.74 87.9672 32.892C87.5672 33.044 87.1312 33.12 86.6592 33.12Z" fill="black"/>
        <circle cx="30" cy="51" r="6" fill="#00A7FF"/>
        <path d="M45.2503 55.12C44.6423 55.12 44.0783 55.016 43.5583 54.808C43.0463 54.6 42.5983 54.308 42.2143 53.932C41.8303 53.548 41.5303 53.096 41.3143 52.576C41.0983 52.056 40.9903 51.484 40.9903 50.86C40.9903 50.236 41.0983 49.664 41.3143 49.144C41.5303 48.624 41.8303 48.176 42.2143 47.8C42.5983 47.416 43.0463 47.12 43.5583 46.912C44.0783 46.704 44.6423 46.6 45.2503 46.6C45.8583 46.6 46.4183 46.704 46.9303 46.912C47.4503 47.12 47.9023 47.416 48.2863 47.8C48.6703 48.176 48.9663 48.624 49.1743 49.144C49.3903 49.664 49.4983 50.236 49.4983 50.86C49.4983 51.484 49.3903 52.056 49.1743 52.576C48.9663 53.096 48.6703 53.548 48.2863 53.932C47.9023 54.308 47.4503 54.6 46.9303 54.808C46.4183 55.016 45.8583 55.12 45.2503 55.12ZM45.2503 53.98C45.6743 53.98 46.0623 53.908 46.4143 53.764C46.7663 53.62 47.0663 53.416 47.3143 53.152C47.5703 52.88 47.7703 52.552 47.9143 52.168C48.0583 51.784 48.1303 51.356 48.1303 50.884C48.1303 50.26 48.0063 49.72 47.7583 49.264C47.5103 48.808 47.1663 48.456 46.7263 48.208C46.2943 47.96 45.8023 47.836 45.2503 47.836C44.8343 47.836 44.4503 47.908 44.0983 48.052C43.7463 48.188 43.4423 48.388 43.1863 48.652C42.9303 48.916 42.7303 49.24 42.5863 49.624C42.4423 50 42.3703 50.42 42.3703 50.884C42.3703 51.516 42.4903 52.064 42.7303 52.528C42.9783 52.992 43.3183 53.352 43.7503 53.608C44.1903 53.856 44.6903 53.98 45.2503 53.98ZM47.6263 55.72C47.5703 55.504 47.5063 55.304 47.4343 55.12C47.3623 54.936 47.2903 54.764 47.2183 54.604C47.1543 54.436 47.0783 54.276 46.9903 54.124L46.9063 53.932C46.7143 53.596 46.4783 53.34 46.1983 53.164C45.9263 52.988 45.6103 52.9 45.2503 52.9C44.8983 52.9 44.5863 52.972 44.3143 53.116C44.0423 53.252 43.7703 53.484 43.4983 53.812L42.8143 53.116C43.0783 52.732 43.4103 52.424 43.8103 52.192C44.2103 51.96 44.6823 51.844 45.2263 51.844C45.7623 51.844 46.2303 51.96 46.6303 52.192C47.0303 52.416 47.3823 52.732 47.6863 53.14L47.9383 53.404C48.1223 53.66 48.2863 53.952 48.4303 54.28C48.5743 54.608 48.7063 54.972 48.8263 55.372L47.6263 55.72ZM53.0497 55.12C52.6097 55.12 52.2177 55.024 51.8737 54.832C51.5297 54.632 51.2577 54.348 51.0577 53.98C50.8577 53.604 50.7577 53.156 50.7577 52.636V49.108H52.0777V52.48C52.0777 52.976 52.1937 53.356 52.4257 53.62C52.6577 53.876 52.9737 54.004 53.3737 54.004C53.6697 54.004 53.9257 53.94 54.1417 53.812C54.3657 53.684 54.5377 53.504 54.6577 53.272C54.7857 53.04 54.8497 52.772 54.8497 52.468V49.108H56.1577V55H54.8857V54.136C54.6857 54.464 54.4297 54.712 54.1177 54.88C53.8057 55.04 53.4497 55.12 53.0497 55.12ZM57.8143 55V49.108H59.1103V55H57.8143ZM58.4623 48.232C58.2383 48.232 58.0543 48.16 57.9103 48.016C57.7663 47.864 57.6943 47.68 57.6943 47.464C57.6943 47.248 57.7663 47.068 57.9103 46.924C58.0543 46.78 58.2383 46.708 58.4623 46.708C58.6863 46.708 58.8703 46.78 59.0143 46.924C59.1583 47.068 59.2303 47.248 59.2303 47.464C59.2303 47.68 59.1583 47.864 59.0143 48.016C58.8703 48.16 58.6863 48.232 58.4623 48.232ZM63.4315 55.12C62.8475 55.12 62.3235 54.988 61.8595 54.724C61.3955 54.46 61.0315 54.096 60.7675 53.632C60.5035 53.168 60.3715 52.64 60.3715 52.048C60.3715 51.608 60.4475 51.204 60.5995 50.836C60.7515 50.46 60.9635 50.136 61.2355 49.864C61.5155 49.584 61.8395 49.368 62.2075 49.216C62.5835 49.064 62.9915 48.988 63.4315 48.988C63.8795 48.988 64.2955 49.064 64.6795 49.216C65.0635 49.36 65.3955 49.576 65.6755 49.864L64.8715 50.704C64.6635 50.48 64.4475 50.32 64.2235 50.224C63.9995 50.128 63.7355 50.08 63.4315 50.08C63.0875 50.08 62.7835 50.168 62.5195 50.344C62.2555 50.512 62.0475 50.744 61.8955 51.04C61.7515 51.328 61.6795 51.664 61.6795 52.048C61.6795 52.432 61.7515 52.772 61.8955 53.068C62.0475 53.364 62.2555 53.6 62.5195 53.776C62.7835 53.944 63.0875 54.028 63.4315 54.028C63.7435 54.028 64.0155 53.976 64.2475 53.872C64.4795 53.768 64.7035 53.604 64.9195 53.38L65.6875 54.22C65.4075 54.508 65.0715 54.732 64.6795 54.892C64.2955 55.044 63.8795 55.12 63.4315 55.12ZM68.0049 54.064L67.8729 52.528C67.9209 52.368 67.9889 52.224 68.0769 52.096C68.1729 51.968 68.2809 51.84 68.4009 51.712L70.8129 49.108H72.3489L68.3049 53.356C68.1929 53.476 68.1129 53.584 68.0649 53.68C68.0249 53.768 68.0049 53.896 68.0049 54.064ZM66.7089 55V46.72H68.0049V55H66.7089ZM70.9929 55L68.8689 51.724L69.9489 51.124L72.5049 55H70.9929ZM76.6729 55V46.72H78.3769L80.7769 51.484L83.1649 46.72H84.8569V55H83.5609V49C83.5609 48.928 83.5609 48.86 83.5609 48.796C83.5609 48.724 83.5649 48.656 83.5729 48.592C83.5569 48.632 83.5369 48.676 83.5129 48.724C83.4889 48.772 83.4609 48.82 83.4289 48.868C83.4049 48.916 83.3809 48.964 83.3569 49.012L81.3769 52.96H80.1649L78.1729 48.988C78.1329 48.924 78.0969 48.856 78.0649 48.784C78.0329 48.712 77.9969 48.648 77.9569 48.592C77.9649 48.656 77.9689 48.724 77.9689 48.796C77.9689 48.868 77.9689 48.94 77.9689 49.012V55H76.6729ZM89.3665 55.12C88.7665 55.12 88.2305 54.984 87.7585 54.712C87.2865 54.44 86.9145 54.072 86.6425 53.608C86.3785 53.136 86.2465 52.616 86.2465 52.048C86.2465 51.616 86.3225 51.216 86.4745 50.848C86.6265 50.472 86.8425 50.148 87.1225 49.876C87.4025 49.596 87.7305 49.38 88.1065 49.228C88.4905 49.068 88.9105 48.988 89.3665 48.988C89.9745 48.988 90.5105 49.124 90.9745 49.396C91.4465 49.668 91.8145 50.036 92.0785 50.5C92.3505 50.956 92.4865 51.472 92.4865 52.048C92.4865 52.48 92.4105 52.884 92.2585 53.26C92.1065 53.628 91.8905 53.952 91.6105 54.232C91.3305 54.512 91.0025 54.732 90.6265 54.892C90.2505 55.044 89.8305 55.12 89.3665 55.12ZM89.3665 54.028C89.7185 54.028 90.0305 53.944 90.3025 53.776C90.5745 53.608 90.7905 53.376 90.9505 53.08C91.1105 52.776 91.1905 52.432 91.1905 52.048C91.1905 51.664 91.1105 51.324 90.9505 51.028C90.7905 50.732 90.5745 50.5 90.3025 50.332C90.0305 50.164 89.7185 50.08 89.3665 50.08C89.0225 50.08 88.7105 50.164 88.4305 50.332C88.1585 50.5 87.9425 50.732 87.7825 51.028C87.6305 51.324 87.5545 51.664 87.5545 52.048C87.5545 52.432 87.6305 52.776 87.7825 53.08C87.9425 53.376 88.1585 53.608 88.4305 53.776C88.7105 53.944 89.0225 54.028 89.3665 54.028ZM95.0696 55L92.8136 49.108H94.2056L95.7176 53.236C95.7496 53.332 95.7776 53.416 95.8016 53.488C95.8336 53.552 95.8656 53.632 95.8976 53.728C95.9296 53.632 95.9576 53.548 95.9816 53.476C96.0136 53.404 96.0416 53.324 96.0656 53.236L97.5296 49.108H98.8976L96.6296 55H95.0696ZM102.423 55.12C101.791 55.12 101.235 54.992 100.755 54.736C100.275 54.472 99.8988 54.112 99.6268 53.656C99.3628 53.192 99.2308 52.656 99.2308 52.048C99.2308 51.608 99.3028 51.204 99.4468 50.836C99.5908 50.46 99.7988 50.136 100.071 49.864C100.343 49.584 100.659 49.368 101.019 49.216C101.379 49.064 101.775 48.988 102.207 48.988C102.783 48.988 103.279 49.112 103.695 49.36C104.119 49.6 104.443 49.94 104.667 50.38C104.899 50.82 105.015 51.332 105.015 51.916C105.015 51.972 105.015 52.028 105.015 52.084C105.015 52.132 105.015 52.176 105.015 52.216C105.015 52.248 105.011 52.284 105.003 52.324H100.383L100.395 51.436H103.743C103.695 50.996 103.535 50.648 103.263 50.392C102.999 50.128 102.647 49.996 102.207 49.996C101.871 49.996 101.575 50.084 101.319 50.26C101.071 50.428 100.875 50.664 100.731 50.968C100.595 51.264 100.527 51.608 100.527 52C100.527 52.424 100.607 52.792 100.767 53.104C100.927 53.416 101.151 53.656 101.439 53.824C101.735 53.992 102.079 54.076 102.471 54.076C102.799 54.076 103.103 54.024 103.383 53.92C103.671 53.808 103.931 53.64 104.163 53.416L104.787 54.244C104.491 54.524 104.139 54.74 103.731 54.892C103.331 55.044 102.895 55.12 102.423 55.12ZM106.218 52.144V51.004H109.71V52.144H106.218ZM111.208 55V46.72H112.54V55H111.208ZM114.334 55V49.108H115.594V49.948C115.81 49.628 116.078 49.388 116.398 49.228C116.726 49.068 117.098 48.988 117.514 48.988C117.986 48.988 118.39 49.088 118.726 49.288C119.062 49.48 119.318 49.76 119.494 50.128C119.678 50.496 119.77 50.94 119.77 51.46V55H118.474V51.604C118.474 51.108 118.362 50.732 118.138 50.476C117.914 50.22 117.586 50.092 117.154 50.092C116.858 50.092 116.594 50.156 116.362 50.284C116.13 50.412 115.95 50.592 115.822 50.824C115.694 51.048 115.63 51.312 115.63 51.616V55H114.334Z" fill="black"/>
        <circle cx="30" cy="73" r="6" fill="#FF9500"/>
        <path d="M41.3155 77V68.72H43.0195L45.4195 73.484L47.8075 68.72H49.4995V77H48.2035V71C48.2035 70.928 48.2035 70.86 48.2035 70.796C48.2035 70.724 48.2075 70.656 48.2155 70.592C48.1995 70.632 48.1795 70.676 48.1555 70.724C48.1315 70.772 48.1035 70.82 48.0715 70.868C48.0475 70.916 48.0235 70.964 47.9995 71.012L46.0195 74.96H44.8075L42.8155 70.988C42.7755 70.924 42.7395 70.856 42.7075 70.784C42.6755 70.712 42.6395 70.648 42.5995 70.592C42.6075 70.656 42.6115 70.724 42.6115 70.796C42.6115 70.868 42.6115 70.94 42.6115 71.012V77H41.3155ZM54.009 77.12C53.409 77.12 52.873 76.984 52.401 76.712C51.929 76.44 51.557 76.072 51.285 75.608C51.021 75.136 50.889 74.616 50.889 74.048C50.889 73.616 50.965 73.216 51.117 72.848C51.269 72.472 51.485 72.148 51.765 71.876C52.045 71.596 52.373 71.38 52.749 71.228C53.133 71.068 53.553 70.988 54.009 70.988C54.617 70.988 55.153 71.124 55.617 71.396C56.089 71.668 56.457 72.036 56.721 72.5C56.993 72.956 57.129 73.472 57.129 74.048C57.129 74.48 57.053 74.884 56.901 75.26C56.749 75.628 56.533 75.952 56.253 76.232C55.973 76.512 55.645 76.732 55.269 76.892C54.893 77.044 54.473 77.12 54.009 77.12ZM54.009 76.028C54.361 76.028 54.673 75.944 54.945 75.776C55.217 75.608 55.433 75.376 55.593 75.08C55.753 74.776 55.833 74.432 55.833 74.048C55.833 73.664 55.753 73.324 55.593 73.028C55.433 72.732 55.217 72.5 54.945 72.332C54.673 72.164 54.361 72.08 54.009 72.08C53.665 72.08 53.353 72.164 53.073 72.332C52.801 72.5 52.585 72.732 52.425 73.028C52.273 73.324 52.197 73.664 52.197 74.048C52.197 74.432 52.273 74.776 52.425 75.08C52.585 75.376 52.801 75.608 53.073 75.776C53.353 75.944 53.665 76.028 54.009 76.028ZM60.8226 77.12C60.2786 77.12 59.7906 76.988 59.3586 76.724C58.9346 76.452 58.5986 76.084 58.3506 75.62C58.1106 75.156 57.9906 74.632 57.9906 74.048C57.9906 73.608 58.0586 73.204 58.1946 72.836C58.3386 72.46 58.5386 72.136 58.7946 71.864C59.0506 71.584 59.3506 71.368 59.6946 71.216C60.0386 71.064 60.4146 70.988 60.8226 70.988C61.1026 70.988 61.3626 71.028 61.6026 71.108C61.8506 71.188 62.0786 71.304 62.2866 71.456C62.4946 71.608 62.6786 71.796 62.8386 72.02V68.72H64.1346V77H62.8626V76.004C62.6226 76.364 62.3266 76.64 61.9746 76.832C61.6226 77.024 61.2386 77.12 60.8226 77.12ZM61.0746 76.028C61.3306 76.028 61.5666 75.98 61.7826 75.884C61.9986 75.78 62.1826 75.64 62.3346 75.464C62.4946 75.288 62.6186 75.08 62.7066 74.84C62.8026 74.592 62.8506 74.328 62.8506 74.048C62.8506 73.672 62.7706 73.336 62.6106 73.04C62.4586 72.744 62.2506 72.512 61.9866 72.344C61.7226 72.168 61.4186 72.08 61.0746 72.08C60.7306 72.08 60.4226 72.168 60.1506 72.344C59.8866 72.512 59.6786 72.744 59.5266 73.04C59.3746 73.336 59.2986 73.672 59.2986 74.048C59.2986 74.424 59.3746 74.764 59.5266 75.068C59.6786 75.364 59.8866 75.6 60.1506 75.776C60.4226 75.944 60.7306 76.028 61.0746 76.028ZM68.5888 77.12C67.9568 77.12 67.4008 76.992 66.9208 76.736C66.4408 76.472 66.0648 76.112 65.7928 75.656C65.5288 75.192 65.3968 74.656 65.3968 74.048C65.3968 73.608 65.4688 73.204 65.6128 72.836C65.7568 72.46 65.9648 72.136 66.2368 71.864C66.5088 71.584 66.8248 71.368 67.1848 71.216C67.5448 71.064 67.9408 70.988 68.3728 70.988C68.9488 70.988 69.4448 71.112 69.8608 71.36C70.2848 71.6 70.6088 71.94 70.8328 72.38C71.0648 72.82 71.1808 73.332 71.1808 73.916C71.1808 73.972 71.1808 74.028 71.1808 74.084C71.1808 74.132 71.1808 74.176 71.1808 74.216C71.1808 74.248 71.1768 74.284 71.1688 74.324H66.5488L66.5608 73.436H69.9088C69.8608 72.996 69.7008 72.648 69.4288 72.392C69.1648 72.128 68.8128 71.996 68.3728 71.996C68.0368 71.996 67.7408 72.084 67.4848 72.26C67.2368 72.428 67.0408 72.664 66.8968 72.968C66.7608 73.264 66.6928 73.608 66.6928 74C66.6928 74.424 66.7728 74.792 66.9328 75.104C67.0928 75.416 67.3168 75.656 67.6048 75.824C67.9008 75.992 68.2448 76.076 68.6368 76.076C68.9648 76.076 69.2688 76.024 69.5488 75.92C69.8368 75.808 70.0968 75.64 70.3288 75.416L70.9528 76.244C70.6568 76.524 70.3048 76.74 69.8968 76.892C69.4968 77.044 69.0608 77.12 68.5888 77.12ZM72.4374 77V68.72H73.7334V77H72.4374ZM84.1946 77V68.72H85.5266V77H84.1946ZM78.6866 77V68.72H80.0186V77H78.6866ZM79.7426 73.388V72.164H84.3266V73.388H79.7426ZM90.0442 77.12C89.4442 77.12 88.9082 76.984 88.4362 76.712C87.9642 76.44 87.5922 76.072 87.3202 75.608C87.0562 75.136 86.9242 74.616 86.9242 74.048C86.9242 73.616 87.0002 73.216 87.1522 72.848C87.3042 72.472 87.5202 72.148 87.8002 71.876C88.0802 71.596 88.4082 71.38 88.7842 71.228C89.1682 71.068 89.5882 70.988 90.0442 70.988C90.6522 70.988 91.1882 71.124 91.6522 71.396C92.1242 71.668 92.4922 72.036 92.7562 72.5C93.0282 72.956 93.1642 73.472 93.1642 74.048C93.1642 74.48 93.0882 74.884 92.9362 75.26C92.7842 75.628 92.5682 75.952 92.2882 76.232C92.0082 76.512 91.6802 76.732 91.3042 76.892C90.9282 77.044 90.5082 77.12 90.0442 77.12ZM90.0442 76.028C90.3962 76.028 90.7082 75.944 90.9802 75.776C91.2522 75.608 91.4682 75.376 91.6282 75.08C91.7882 74.776 91.8682 74.432 91.8682 74.048C91.8682 73.664 91.7882 73.324 91.6282 73.028C91.4682 72.732 91.2522 72.5 90.9802 72.332C90.7082 72.164 90.3962 72.08 90.0442 72.08C89.7002 72.08 89.3882 72.164 89.1082 72.332C88.8362 72.5 88.6202 72.732 88.4602 73.028C88.3082 73.324 88.2322 73.664 88.2322 74.048C88.2322 74.432 88.3082 74.776 88.4602 75.08C88.6202 75.376 88.8362 75.608 89.1082 75.776C89.3882 75.944 89.7002 76.028 90.0442 76.028ZM94.4218 77V71.108H95.6818V71.96C95.8738 71.648 96.1178 71.412 96.4138 71.252C96.7098 71.084 97.0498 70.996 97.4338 70.988C97.7938 70.972 98.1178 71.036 98.4058 71.18C98.7018 71.324 98.9458 71.528 99.1378 71.792C99.3378 72.048 99.4698 72.344 99.5338 72.68L99.1498 72.62C99.2618 72.26 99.4258 71.96 99.6418 71.72C99.8658 71.48 100.126 71.3 100.422 71.18C100.718 71.052 101.042 70.988 101.394 70.988C101.85 70.988 102.246 71.092 102.582 71.3C102.918 71.5 103.178 71.784 103.362 72.152C103.546 72.52 103.638 72.956 103.638 73.46V77H102.342V73.604C102.342 73.14 102.226 72.772 101.994 72.5C101.762 72.228 101.442 72.092 101.034 72.092C100.778 72.092 100.546 72.16 100.338 72.296C100.13 72.424 99.9658 72.604 99.8458 72.836C99.7338 73.06 99.6778 73.32 99.6778 73.616V77H98.3818V73.604C98.3818 73.148 98.2658 72.784 98.0338 72.512C97.8018 72.232 97.4858 72.092 97.0858 72.092C96.8218 72.092 96.5858 72.16 96.3778 72.296C96.1698 72.424 96.0058 72.604 95.8858 72.836C95.7738 73.06 95.7178 73.32 95.7178 73.616V77H94.4218ZM108.011 77.12C107.379 77.12 106.823 76.992 106.343 76.736C105.863 76.472 105.487 76.112 105.215 75.656C104.951 75.192 104.819 74.656 104.819 74.048C104.819 73.608 104.891 73.204 105.035 72.836C105.179 72.46 105.387 72.136 105.659 71.864C105.931 71.584 106.247 71.368 106.607 71.216C106.967 71.064 107.363 70.988 107.795 70.988C108.371 70.988 108.867 71.112 109.283 71.36C109.707 71.6 110.031 71.94 110.255 72.38C110.487 72.82 110.603 73.332 110.603 73.916C110.603 73.972 110.603 74.028 110.603 74.084C110.603 74.132 110.603 74.176 110.603 74.216C110.603 74.248 110.599 74.284 110.591 74.324H105.971L105.983 73.436H109.331C109.283 72.996 109.123 72.648 108.851 72.392C108.587 72.128 108.235 71.996 107.795 71.996C107.459 71.996 107.163 72.084 106.907 72.26C106.659 72.428 106.463 72.664 106.319 72.968C106.183 73.264 106.115 73.608 106.115 74C106.115 74.424 106.195 74.792 106.355 75.104C106.515 75.416 106.739 75.656 107.027 75.824C107.323 75.992 107.667 76.076 108.059 76.076C108.387 76.076 108.691 76.024 108.971 75.92C109.259 75.808 109.519 75.64 109.751 75.416L110.375 76.244C110.079 76.524 109.727 76.74 109.319 76.892C108.919 77.044 108.483 77.12 108.011 77.12ZM113.863 77.12C113.351 77.12 112.891 77.048 112.483 76.904C112.075 76.752 111.711 76.528 111.391 76.232L111.991 75.356C112.263 75.604 112.547 75.792 112.843 75.92C113.147 76.04 113.479 76.1 113.839 76.1C114.239 76.1 114.539 76.04 114.739 75.92C114.947 75.792 115.051 75.612 115.051 75.38C115.051 75.244 115.019 75.132 114.955 75.044C114.899 74.956 114.787 74.88 114.619 74.816C114.459 74.744 114.219 74.668 113.899 74.588L113.611 74.516C112.891 74.34 112.367 74.124 112.039 73.868C111.719 73.604 111.559 73.232 111.559 72.752C111.559 72.384 111.655 72.068 111.847 71.804C112.039 71.54 112.311 71.34 112.663 71.204C113.015 71.06 113.435 70.988 113.923 70.988C114.403 70.988 114.827 71.056 115.195 71.192C115.563 71.328 115.899 71.528 116.203 71.792L115.603 72.668C115.339 72.452 115.075 72.288 114.811 72.176C114.555 72.056 114.259 71.996 113.923 71.996C113.571 71.996 113.299 72.06 113.107 72.188C112.915 72.316 112.819 72.492 112.819 72.716C112.819 72.908 112.903 73.06 113.071 73.172C113.239 73.276 113.551 73.388 114.007 73.508L114.307 73.58C115.019 73.756 115.531 73.968 115.843 74.216C116.155 74.464 116.311 74.828 116.311 75.308C116.311 75.868 116.087 76.312 115.639 76.64C115.199 76.96 114.607 77.12 113.863 77.12Z" fill="black"/>
        <circle cx="30" cy="95" r="6" fill="#C7C7C7"/>
        <path d="M43.8562 99.108C43.3922 99.108 42.9562 99.056 42.5482 98.952C42.1402 98.848 41.7682 98.692 41.4322 98.484C41.0962 98.276 40.8042 98.024 40.5562 97.728L41.4442 96.864C41.7882 97.248 42.1482 97.528 42.5242 97.704C42.9082 97.872 43.3602 97.956 43.8802 97.956C44.4002 97.956 44.8042 97.856 45.0922 97.656C45.3882 97.448 45.5362 97.152 45.5362 96.768C45.5362 96.552 45.4882 96.368 45.3922 96.216C45.3042 96.064 45.1482 95.932 44.9242 95.82C44.7002 95.708 44.3962 95.604 44.0122 95.508L43.2682 95.34C42.4202 95.14 41.7962 94.856 41.3962 94.488C40.9962 94.112 40.7962 93.616 40.7962 93C40.7962 92.504 40.9162 92.076 41.1562 91.716C41.3962 91.356 41.7362 91.08 42.1762 90.888C42.6242 90.696 43.1522 90.6 43.7602 90.6C44.3682 90.6 44.9202 90.704 45.4162 90.912C45.9122 91.12 46.3522 91.436 46.7362 91.86L45.8482 92.724C45.6242 92.476 45.4002 92.28 45.1762 92.136C44.9602 91.992 44.7282 91.892 44.4802 91.836C44.2402 91.78 43.9642 91.752 43.6522 91.752C43.1802 91.752 42.8042 91.856 42.5242 92.064C42.2522 92.272 42.1162 92.56 42.1162 92.928C42.1162 93.232 42.2282 93.48 42.4522 93.672C42.6842 93.856 43.0682 94.012 43.6042 94.14L44.3482 94.308C45.2362 94.524 45.8762 94.82 46.2682 95.196C46.6682 95.572 46.8682 96.076 46.8682 96.708C46.8682 97.196 46.7442 97.62 46.4962 97.98C46.2482 98.34 45.9002 98.62 45.4522 98.82C45.0042 99.012 44.4722 99.108 43.8562 99.108ZM50.9309 99.12C50.3309 99.12 49.7949 98.984 49.3229 98.712C48.8509 98.44 48.4789 98.072 48.2069 97.608C47.9429 97.136 47.8109 96.616 47.8109 96.048C47.8109 95.616 47.8869 95.216 48.0389 94.848C48.1909 94.472 48.4069 94.148 48.6869 93.876C48.9669 93.596 49.2949 93.38 49.6709 93.228C50.0549 93.068 50.4749 92.988 50.9309 92.988C51.5389 92.988 52.0749 93.124 52.5389 93.396C53.0109 93.668 53.3789 94.036 53.6429 94.5C53.9149 94.956 54.0509 95.472 54.0509 96.048C54.0509 96.48 53.9749 96.884 53.8229 97.26C53.6709 97.628 53.4549 97.952 53.1749 98.232C52.8949 98.512 52.5669 98.732 52.1909 98.892C51.8149 99.044 51.3949 99.12 50.9309 99.12ZM50.9309 98.028C51.2829 98.028 51.5949 97.944 51.8669 97.776C52.1389 97.608 52.3549 97.376 52.5149 97.08C52.6749 96.776 52.7549 96.432 52.7549 96.048C52.7549 95.664 52.6749 95.324 52.5149 95.028C52.3549 94.732 52.1389 94.5 51.8669 94.332C51.5949 94.164 51.2829 94.08 50.9309 94.08C50.5869 94.08 50.2749 94.164 49.9949 94.332C49.7229 94.5 49.5069 94.732 49.3469 95.028C49.1949 95.324 49.1189 95.664 49.1189 96.048C49.1189 96.432 49.1949 96.776 49.3469 97.08C49.5069 97.376 49.7229 97.608 49.9949 97.776C50.2749 97.944 50.5869 98.028 50.9309 98.028ZM55.3085 99V90.72H56.6045V99H55.3085ZM60.6976 99.12C60.1536 99.12 59.6656 98.988 59.2336 98.724C58.8096 98.452 58.4736 98.084 58.2256 97.62C57.9856 97.156 57.8656 96.632 57.8656 96.048C57.8656 95.608 57.9336 95.204 58.0696 94.836C58.2136 94.46 58.4136 94.136 58.6696 93.864C58.9256 93.584 59.2256 93.368 59.5696 93.216C59.9136 93.064 60.2896 92.988 60.6976 92.988C60.9776 92.988 61.2376 93.028 61.4776 93.108C61.7256 93.188 61.9536 93.304 62.1616 93.456C62.3696 93.608 62.5536 93.796 62.7136 94.02V90.72H64.0096V99H62.7376V98.004C62.4976 98.364 62.2016 98.64 61.8496 98.832C61.4976 99.024 61.1136 99.12 60.6976 99.12ZM60.9496 98.028C61.2056 98.028 61.4416 97.98 61.6576 97.884C61.8736 97.78 62.0576 97.64 62.2096 97.464C62.3696 97.288 62.4936 97.08 62.5816 96.84C62.6776 96.592 62.7256 96.328 62.7256 96.048C62.7256 95.672 62.6456 95.336 62.4856 95.04C62.3336 94.744 62.1256 94.512 61.8616 94.344C61.5976 94.168 61.2936 94.08 60.9496 94.08C60.6056 94.08 60.2976 94.168 60.0256 94.344C59.7616 94.512 59.5536 94.744 59.4016 95.04C59.2496 95.336 59.1736 95.672 59.1736 96.048C59.1736 96.424 59.2496 96.764 59.4016 97.068C59.5536 97.364 59.7616 97.6 60.0256 97.776C60.2976 97.944 60.6056 98.028 60.9496 98.028Z" fill="black"/>
        <circle cx="30" cy="117" r="6" fill="#E7E7E7"/>
        <path d="M44.6311 121.12C43.9191 121.112 43.3111 120.972 42.8071 120.7C42.3031 120.428 41.9191 120.032 41.6551 119.512C41.3911 118.984 41.2591 118.356 41.2591 117.628V112.72H42.5911V117.496C42.5911 118.008 42.6711 118.444 42.8311 118.804C42.9911 119.156 43.2231 119.424 43.5271 119.608C43.8311 119.792 44.2071 119.884 44.6551 119.884C45.3191 119.876 45.8231 119.676 46.1671 119.284C46.5111 118.884 46.6831 118.304 46.6831 117.544V112.72H48.0151V117.58C48.0151 118.7 47.7191 119.568 47.1271 120.184C46.5431 120.8 45.7111 121.112 44.6311 121.12ZM49.7303 121V115.108H50.9903V115.948C51.2063 115.628 51.4743 115.388 51.7943 115.228C52.1223 115.068 52.4943 114.988 52.9103 114.988C53.3823 114.988 53.7863 115.088 54.1223 115.288C54.4583 115.48 54.7143 115.76 54.8903 116.128C55.0743 116.496 55.1663 116.94 55.1663 117.46V121H53.8703V117.604C53.8703 117.108 53.7583 116.732 53.5343 116.476C53.3103 116.22 52.9823 116.092 52.5503 116.092C52.2543 116.092 51.9903 116.156 51.7583 116.284C51.5263 116.412 51.3463 116.592 51.2183 116.824C51.0903 117.048 51.0263 117.312 51.0263 117.616V121H49.7303ZM58.2262 121.12C57.6102 121.12 57.1262 120.968 56.7742 120.664C56.4222 120.36 56.2462 119.944 56.2462 119.416C56.2462 119.08 56.3222 118.796 56.4742 118.564C56.6342 118.332 56.8702 118.144 57.1822 118C57.4942 117.848 57.8902 117.736 58.3702 117.664L59.3182 117.508C59.6222 117.46 59.8342 117.392 59.9542 117.304C60.0742 117.216 60.1342 117.08 60.1342 116.896C60.1342 116.64 60.0222 116.432 59.7982 116.272C59.5822 116.112 59.2822 116.032 58.8982 116.032C58.5702 116.032 58.2502 116.096 57.9382 116.224C57.6342 116.352 57.3582 116.54 57.1102 116.788L56.4382 115.972C56.7502 115.644 57.1222 115.4 57.5542 115.24C57.9862 115.072 58.4622 114.988 58.9822 114.988C59.7582 114.988 60.3542 115.172 60.7702 115.54C61.1862 115.9 61.3942 116.408 61.3942 117.064V119.512C61.3942 119.848 61.4222 120.124 61.4782 120.34C61.5342 120.548 61.6382 120.768 61.7902 121H60.4942C60.3982 120.856 60.3262 120.72 60.2782 120.592C60.2382 120.456 60.2102 120.28 60.1942 120.064C60.0582 120.288 59.8902 120.48 59.6902 120.64C59.4902 120.8 59.2662 120.92 59.0182 121C58.7782 121.08 58.5142 121.12 58.2262 121.12ZM58.5262 120.124C58.8382 120.124 59.1142 120.06 59.3542 119.932C59.6022 119.804 59.7942 119.628 59.9302 119.404C60.0662 119.172 60.1342 118.908 60.1342 118.612V118.12C60.0462 118.184 59.9462 118.236 59.8342 118.276C59.7302 118.316 59.5902 118.352 59.4142 118.384L58.6342 118.492C58.2502 118.556 57.9662 118.652 57.7822 118.78C57.5982 118.908 57.5062 119.092 57.5062 119.332C57.5062 119.596 57.5942 119.796 57.7702 119.932C57.9462 120.06 58.1982 120.124 58.5262 120.124ZM64.3919 121L62.1359 115.108H63.5279L65.0399 119.236C65.0719 119.332 65.0999 119.416 65.1239 119.488C65.1559 119.552 65.1879 119.632 65.2199 119.728C65.2519 119.632 65.2799 119.548 65.3039 119.476C65.3359 119.404 65.3639 119.324 65.3879 119.236L66.8519 115.108H68.2199L65.9519 121H64.3919ZM70.4957 121.12C69.8797 121.12 69.3957 120.968 69.0437 120.664C68.6917 120.36 68.5157 119.944 68.5157 119.416C68.5157 119.08 68.5917 118.796 68.7437 118.564C68.9037 118.332 69.1397 118.144 69.4517 118C69.7637 117.848 70.1597 117.736 70.6397 117.664L71.5877 117.508C71.8917 117.46 72.1037 117.392 72.2237 117.304C72.3437 117.216 72.4037 117.08 72.4037 116.896C72.4037 116.64 72.2917 116.432 72.0677 116.272C71.8517 116.112 71.5517 116.032 71.1677 116.032C70.8397 116.032 70.5197 116.096 70.2077 116.224C69.9037 116.352 69.6277 116.54 69.3797 116.788L68.7077 115.972C69.0197 115.644 69.3917 115.4 69.8237 115.24C70.2557 115.072 70.7317 114.988 71.2517 114.988C72.0277 114.988 72.6237 115.172 73.0397 115.54C73.4557 115.9 73.6637 116.408 73.6637 117.064V119.512C73.6637 119.848 73.6917 120.124 73.7477 120.34C73.8037 120.548 73.9077 120.768 74.0597 121H72.7637C72.6677 120.856 72.5957 120.72 72.5477 120.592C72.5077 120.456 72.4797 120.28 72.4637 120.064C72.3277 120.288 72.1597 120.48 71.9597 120.64C71.7597 120.8 71.5357 120.92 71.2877 121C71.0477 121.08 70.7837 121.12 70.4957 121.12ZM70.7957 120.124C71.1077 120.124 71.3837 120.06 71.6237 119.932C71.8717 119.804 72.0637 119.628 72.1997 119.404C72.3357 119.172 72.4037 118.908 72.4037 118.612V118.12C72.3157 118.184 72.2157 118.236 72.1037 118.276C71.9997 118.316 71.8597 118.352 71.6837 118.384L70.9037 118.492C70.5197 118.556 70.2357 118.652 70.0517 118.78C69.8677 118.908 69.7757 119.092 69.7757 119.332C69.7757 119.596 69.8637 119.796 70.0397 119.932C70.2157 120.06 70.4677 120.124 70.7957 120.124ZM75.2772 121V115.108H76.5732V121H75.2772ZM75.9252 114.232C75.7012 114.232 75.5172 114.16 75.3732 114.016C75.2292 113.864 75.1572 113.68 75.1572 113.464C75.1572 113.248 75.2292 113.068 75.3732 112.924C75.5172 112.78 75.7012 112.708 75.9252 112.708C76.1492 112.708 76.3332 112.78 76.4772 112.924C76.6212 113.068 76.6932 113.248 76.6932 113.464C76.6932 113.68 76.6212 113.864 76.4772 114.016C76.3332 114.16 76.1492 114.232 75.9252 114.232ZM78.2303 121V112.72H79.5263V121H78.2303ZM82.6715 121.12C82.0555 121.12 81.5715 120.968 81.2195 120.664C80.8675 120.36 80.6915 119.944 80.6915 119.416C80.6915 119.08 80.7675 118.796 80.9195 118.564C81.0795 118.332 81.3155 118.144 81.6275 118C81.9395 117.848 82.3355 117.736 82.8155 117.664L83.7635 117.508C84.0675 117.46 84.2795 117.392 84.3995 117.304C84.5195 117.216 84.5795 117.08 84.5795 116.896C84.5795 116.64 84.4675 116.432 84.2435 116.272C84.0275 116.112 83.7275 116.032 83.3435 116.032C83.0155 116.032 82.6955 116.096 82.3835 116.224C82.0795 116.352 81.8035 116.54 81.5555 116.788L80.8835 115.972C81.1955 115.644 81.5675 115.4 81.9995 115.24C82.4315 115.072 82.9075 114.988 83.4275 114.988C84.2035 114.988 84.7995 115.172 85.2155 115.54C85.6315 115.9 85.8395 116.408 85.8395 117.064V119.512C85.8395 119.848 85.8675 120.124 85.9235 120.34C85.9795 120.548 86.0835 120.768 86.2355 121H84.9395C84.8435 120.856 84.7715 120.72 84.7235 120.592C84.6835 120.456 84.6555 120.28 84.6395 120.064C84.5035 120.288 84.3355 120.48 84.1355 120.64C83.9355 120.8 83.7115 120.92 83.4635 121C83.2235 121.08 82.9595 121.12 82.6715 121.12ZM82.9715 120.124C83.2835 120.124 83.5595 120.06 83.7995 119.932C84.0475 119.804 84.2395 119.628 84.3755 119.404C84.5115 119.172 84.5795 118.908 84.5795 118.612V118.12C84.4915 118.184 84.3915 118.236 84.2795 118.276C84.1755 118.316 84.0355 118.352 83.8595 118.384L83.0795 118.492C82.6955 118.556 82.4115 118.652 82.2275 118.78C82.0435 118.908 81.9515 119.092 81.9515 119.332C81.9515 119.596 82.0395 119.796 82.2155 119.932C82.3915 120.06 82.6435 120.124 82.9715 120.124ZM90.765 121.12C90.477 121.12 90.205 121.08 89.949 121C89.701 120.92 89.473 120.804 89.265 120.652C89.065 120.492 88.881 120.3 88.713 120.076V121H87.453V112.72H88.749V116.092C88.981 115.74 89.273 115.468 89.625 115.276C89.977 115.084 90.357 114.988 90.765 114.988C91.309 114.988 91.793 115.124 92.217 115.396C92.641 115.66 92.977 116.024 93.225 116.488C93.473 116.944 93.597 117.464 93.597 118.048C93.597 118.488 93.525 118.896 93.381 119.272C93.237 119.64 93.037 119.964 92.781 120.244C92.533 120.524 92.237 120.74 91.893 120.892C91.549 121.044 91.173 121.12 90.765 121.12ZM90.513 120.028C90.857 120.028 91.161 119.944 91.425 119.776C91.697 119.6 91.909 119.364 92.061 119.068C92.213 118.764 92.289 118.424 92.289 118.048C92.289 117.672 92.213 117.336 92.061 117.04C91.909 116.744 91.697 116.512 91.425 116.344C91.161 116.168 90.857 116.08 90.513 116.08C90.257 116.08 90.021 116.132 89.805 116.236C89.589 116.332 89.401 116.468 89.241 116.644C89.081 116.82 88.957 117.028 88.869 117.268C88.781 117.508 88.737 117.768 88.737 118.048C88.737 118.424 88.813 118.764 88.965 119.068C89.117 119.364 89.329 119.6 89.601 119.776C89.873 119.944 90.177 120.028 90.513 120.028ZM94.8593 121V112.72H96.1553V121H94.8593ZM100.608 121.12C99.9764 121.12 99.4204 120.992 98.9404 120.736C98.4604 120.472 98.0844 120.112 97.8124 119.656C97.5484 119.192 97.4164 118.656 97.4164 118.048C97.4164 117.608 97.4884 117.204 97.6324 116.836C97.7764 116.46 97.9844 116.136 98.2564 115.864C98.5284 115.584 98.8444 115.368 99.2044 115.216C99.5644 115.064 99.9604 114.988 100.392 114.988C100.968 114.988 101.464 115.112 101.88 115.36C102.304 115.6 102.628 115.94 102.852 116.38C103.084 116.82 103.2 117.332 103.2 117.916C103.2 117.972 103.2 118.028 103.2 118.084C103.2 118.132 103.2 118.176 103.2 118.216C103.2 118.248 103.196 118.284 103.188 118.324H98.5684L98.5804 117.436H101.928C101.88 116.996 101.72 116.648 101.448 116.392C101.184 116.128 100.832 115.996 100.392 115.996C100.056 115.996 99.7604 116.084 99.5044 116.26C99.2564 116.428 99.0604 116.664 98.9164 116.968C98.7804 117.264 98.7124 117.608 98.7124 118C98.7124 118.424 98.7924 118.792 98.9524 119.104C99.1124 119.416 99.3364 119.656 99.6244 119.824C99.9204 119.992 100.264 120.076 100.656 120.076C100.984 120.076 101.288 120.024 101.568 119.92C101.856 119.808 102.116 119.64 102.348 119.416L102.972 120.244C102.676 120.524 102.324 120.74 101.916 120.892C101.516 121.044 101.08 121.12 100.608 121.12Z" fill="black"/>
      </g>
      <defs>
        <filter id="filter0_d_4090_40850" x="0" y="0" width="145" height="154" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
          <feFlood flood-opacity="0" result="BackgroundImageFix"/>
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
          <feOffset dy="4"/>
          <feGaussianBlur stdDeviation="8"/>
          <feComposite in2="hardAlpha" operator="out"/>
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"/>
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_4090_40850"/>
          <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_4090_40850" result="shape"/>
        </filter>
      </defs>
      </svg>
    `;
	}

	// ----------------------------------------------------------------------
	// Outlines / Inactive Lots
	// ----------------------------------------------------------------------

	async drawLotOutlines() {
		return new Promise<void>(async (resolve) => {
			this.outlineLayer = new google.maps.Data();

			this.outlineLayer.loadGeoJson(LOT_OUTLINES_URL, null, () => {
				const overlay = createCanvasOverlay(
					this.map,
					this.outlineLayer,
					CanvasOverlayType.LotOutline
				);

				overlay.setMap(this.map);

				resolve();
			});
		});
	}

	// ----------------------------------------------------------------------
	// Lots
	// ----------------------------------------------------------------------

	async drawLots() {
		return new Promise<void>(async (resolve) => {
			if (this.lotLayer) this.deleteLots();

			// Create a layer for the Lots
			this.lotLayer = new google.maps.Data();

			let url = this.getUrl();
			let parcel_ids: number[] = [];

			// Load the geojson
			this.lotLayer.loadGeoJson(url, null, (features: any) => {
				// Add the lot layer to the map
				this.lotLayer.setMap(this.map);

				this.numLots = features.length;

				features.forEach((feature: any) => {
					const lot = new Lot(feature, this.lotLayer);
					if (lot.parcelId && !parcel_ids.includes(lot.parcelId)) {
						parcel_ids.push(lot.parcelId);
					}
					if (lot.id == this.vars.get<string>("lotmarker")) {
						lot.addMarker();
						if (OPEN_POPUP_FOR_FOCUSED_LOT) this.openLotPopup(lot);
					}
					this.lots.set(feature, lot);
				});

				this.minimap = this.shadow.querySelector("hhds-minimap");
				if (this.minimap) {
					this.minimap.setAttribute("parcel", parcel_ids.join(","));
				}

				// Add interactivity
				this.lotLayer.addListener("click", async (event: any) => {
					const feature = event.feature;
					const lot = this.lots.get(feature);
					this.openLotPopup(lot);
				});

				this.lotLayer.addListener("mouseover", (event: any) => {
					const lot = this.lots.get(event.feature);
					lot?.onHover();
				});

				this.lotLayer.addListener("mouseout", (event: any) => {
					const lot = this.lots.get(event.feature);
					lot?.onLeave();
				});

				resolve();
			});
		});
	}

	async deleteLots() {
		if (this.lotLayer) {
			this.lotLayer.forEach((feature: google.maps.Data.Feature) => {
				const lot = this.lots.get(feature);
				lot?.delete();
				this.lots.delete(feature);
			});
		}
	}

	async openLotPopup(lot?: Lot) {
		if (lot && lot.isInteractive) {
			this.activeLot = lot;
			DEBUG_VERBOSE &&
				console.log(CLASS_NAME, "Active lot is now", this.activeLot);
			const response = await fetch(`/wp-json/teravalis/v1/lots/${lot.id}`);
			const data = await response.json();

			if (data?.features?.length) {
				this.showMapPopup(data.features[0]);
			} else {
				console.error("The API returned no features.");
			}
		} else {
			console.warn("No lot found for clicked feature.");
		}
	}

	// ----------------------------------------------------------------------
	// API Handling
	// ----------------------------------------------------------------------

	getUrl(): string {
		let url: string = API_LOTMAP_ENDPOINT;
		if (this.vars.get("lot")) {
			this.vars.set("lotmarker", this.vars.get("lot"), "string");
			url = API_LOTMAP_ENDPOINT + `/${this.vars.get("lot")}`;
		} else {
			url += "?";

			if (this.vars.get("builder")) {
				url += `builder=${this.vars.get("builder")}`;
			}

			if (this.vars.get("collection")) {
				url += `&collection=${this.vars.get("collection")}`;
			}

			if (this.vars.get("parcel")) {
				url += `&parcel=${this.vars.get("parcel")}`;
			}
		}
		return url;
	}

	// ----------------------------------------------------------------------
	// Animation
	// ----------------------------------------------------------------------

	animateIn() {
		return new Promise<void>(async (resolve) => {
			const tl = gsap.timeline({
				defaults: {
					duration: 0.75,
					ease: "power2.out",
				},
				onComplete: () => {
					resolve();
				},
			});

			tl.to([this.container, this.legend, this.minimap], {
				opacity: 1,
			});

			tl.play();
		});
	}

	animateOut() {
		return new Promise<void>(async (resolve) => {
			const tl = gsap.timeline({
				defaults: {
					duration: 0.75,
					ease: "power2.out",
				},
				onComplete: () => {
					resolve();
				},
			});

			tl.to([this.container, this.legend, this.minimap], {
				opacity: 0,
			});

			tl.play();
		});
	}

	showElement(element?: HTMLElement): Promise<void> {
		return new Promise<void>(async (resolve) => {
			if (!element) {
				alert("No element provided to showElement.");
				resolve();
			} else {
				gsap.to(element, {
					opacity: 1,
					duration: 0.5,
					ease: "power2.out",
					delay: 1,
					onComplete: () => {
						resolve();
					},
				});
			}
		});
	}

	hideElement(element?: HTMLElement): Promise<void> {
		return new Promise<void>(async (resolve) => {
			if (!element) {
				resolve();
			} else {
				gsap.to(element, {
					opacity: 0,
					duration: 0.5,
					ease: "power2.out",
					delay: 1,
					onComplete: () => {
						resolve();
					},
				});
			}
		});
	}

	// ----------------------------------------------------------------------
	// Switcher
	// ----------------------------------------------------------------------

	get switcherHTML(): string {
		const switcher = this.vars.get("switcher");
		if (switcher) {
			let optionsString = "";
			switcher.split(",").map((option: any) => {
				const [value, label] = option.split("|");
				optionsString += `<div value="${value}">${label}</div>`;
			});
			return `<div class="hhds-lotmap-switcher"><hhds-segmented-control selected="${this.vars.get(
				"collection"
			)}">${optionsString}</hhds-segmented-control></div>`;
		}
		return "";
	}

	private setupSwitcher() {
		if (this.vars.get<string>("switcher")) {
			const segmentedControl = this.shadow.querySelector(
				"hhds-segmented-control"
			) as HHDSSegmentedControl;
			segmentedControl?.addEventListener("change", (event: Event) =>
				this.switch(event)
			);
		}
	}

	private fadeOut(): Promise<void> {
		return new Promise((resolve) => {
			gsap.to(this.shadow.querySelector(".hhds-lotmap-container"), {
				opacity: 0,
				duration: 0.5,
				onComplete: () => {
					resolve();
				},
			});
		});
	}

	private fadeIn(): Promise<void> {
		return new Promise((resolve) => {
			gsap.to(this.shadow.querySelector(".hhds-lotmap-container"), {
				opacity: 1,
				duration: 0.5,
				onComplete: () => {
					resolve();
				},
			});
		});
	}

	private async switch(event: Event) {
		if (!this.loading) {
			this.loading = true;
			const control = event.target as HHDSSegmentedControl;
			this.vars.set("collection", control.value, "string");
			this.loading = true;
			await this.fadeOut();
			await this.deleteLots();
			await this.popup?.hide();

			this.setMapBounds();
			await this.drawLots();

			if (this.numLots > 0) {
				mapHelper.zoomToCenterOfLayerBounds(this.lotLayer);
			} else {
				mapHelper.zoomToCenterOfLayerBounds(this.outlineLayer);
			}

			await this.fadeIn();

			this.loading = false;
		}
	}

	// ----------------------------------------------------------------------
	// SVG Overlay
	// ----------------------------------------------------------------------

	setupOverlay() {
		const overlayBounds = new google.maps.LatLngBounds(
			new google.maps.LatLng(DEFAULT_SW_LAT, DEFAULT_SW_LNG),
			new google.maps.LatLng(DEFAULT_NE_LAT, DEFAULT_NE_LNG)
		);
		createSVGOverlay(OVERLAY_URL, overlayBounds, this.map);
	}

	// ----------------------------------------------------------------------
	// Bounds
	// ----------------------------------------------------------------------

	setMapBounds() {
		const bounds = new google.maps.LatLngBounds(
			new google.maps.LatLng(DEFAULT_SW_LAT, DEFAULT_SW_LNG),
			new google.maps.LatLng(DEFAULT_NE_LAT, DEFAULT_NE_LNG)
		);

		this.map.fitBounds(bounds, {
			zoom: 20,
		});

		this.map.setOptions({
			restriction: {
				latLngBounds: bounds,
				strictBounds: false,
			},
		});
		DEBUG_VERBOSE && console.log(CLASS_NAME, "Map bounds set to default.");
	}

	// ----------------------------------------------------------------------
	// Popups
	// ----------------------------------------------------------------------

	private configurePopup() {
		this.popup = this.shadow.querySelector(HHDSMapPopupTagName) as HHDSMapPopup;
		this.popup?.addEventListener(HHDSMapPopupEvent.show, () => {
			// this.focusHandler.updateFocusableElements(this.popup?.focusableElements);
			// this.focusHandler.trap();
		});
		this.popup?.addEventListener(HHDSMapPopupEvent.hide, () => {
			//const refocusedElement = this.focusHandler.untrap();
			//parcelsMap?.unactivateParcels();
			//if (refocusedElement) parcelsMap?.rehighlightParcelIfRequired(refocusedElement.id);

			this.activeLot = null;
		});
		if (this.popup && this.container) {
			const updatePopupHeight = () => {
				// TODO: Remove the need for the -40px offset
				this.popup?.style.setProperty(
					"--map-popup-height",
					`${
						this.container?.clientHeight ? this.container?.clientHeight - 40 : 0
					}px`
				);
			};
			window.addEventListener("resize", () => updatePopupHeight());
			updatePopupHeight();
		}
	}

	private showMapPopup(data: LotApiResponse): void {
		this.popup?.show();
		this.popup?.setSlotContent(this.getPopupContentForFeature(data), "content");
	}

	private getPopupContentForFeature(lot: LotApiResponse): string {
		const data = lot["properties"];

		if (data.lot.model) {
			return getModelHomeContent(lot);
		} else if (data.lot.quickmove) {
			return getPlanContent(lot);
		} else if (data.lot.status === "available") {
			return getAvailableLotContent(lot);
		}

		return "";
	}

	protected override destroy(): void {}

	override onAttributeChanged(
		name: string,
		_oldValue: string,
		newValue: string
	): void {
		DEBUG_VERBOSE &&
			console.log(CLASS_NAME, "Attribute changed: ", name, _oldValue, newValue);
		this.reinit();
	}

	override onSlotChange(_slot: HTMLSlotElement, _elements: Element[]): void {}

	static override argSpecs(): ArgSpecDictionary {
		return ArgSpecs;
	}
}

export const ArgSpecs: ArgSpecDictionary = {
	[Attrs.image]: {
		description: "Overlay image url.",
		defaultValue: "",
		type: String,
	},
	[Attrs.lot]: {
		description: "Lot number.",
		defaultValue: "",
		type: String,
	},
	[Attrs.lotmarker]: {
		description: "Show a marker at the specified Lot.",
		defaultValue: "",
		type: String,
	},

	[Attrs.builder]: {
		description: "Builder Post ID (eg. 74578)",
		defaultValue: "",
		type: String,
	},

	[Attrs.collection]: {
		description: "Collection Slug (eg. kb-homes-collection-01).",
		defaultValue: "",
		type: String,
	},
	[Attrs.parcel]: {
		description:
			"The parcel(s) to highlight on the mini-map. Comma-separated (eg. 123,456).",
		defaultValue: null,
		type: String,
	},
	[Attrs.switcher]: {
		description:
			"Options for the switcher (eg 123|Collection One,456|Collection Two).",
		defaultValue: "",
		type: String,
	},
	[Attrs.geojson]: {
		description: "URL of a GeoJSON file.",
		defaultValue: "",
		type: String,
	},
	[Attrs.controls]: {
		description: "Whether or not the zoom buttons are visible.",
		defaultValue: false,
		type: Boolean,
	},
};
